import React, { useState, useMemo } from 'react'
import { DollarSign, HelpCircle, ChevronDown } from 'lucide-react'
import { formatCurrency, getVarianceColor, getVarianceIconData } from '../../lib/financial-utils'
import { labels, icons, FinancialData } from './financial-config'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { TREND_STATUS_CONFIG, type TrendResult } from '@/lib/services/trend-analysis.service'
import { TrendExplanationModal } from '@/components/ceo/TrendExplanationModal'
import { AIInsightModal } from '@/components/ai/AIInsightModal'
import { CardNotesButton } from '@/components/ai/CardNotesButton'
import { generateInsight } from '@/lib/services/ai-insights.service'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'

export interface MetricCardAdjustmentItem {
  /** Left-hand label, e.g. "Folha Março" or "Custos não pagos" */
  label: string
  /** Value to deduct from the main number (R$) */
  value: number
  /** When true, renders "(aprox.)" next to the value */
  isApproximate?: boolean
  /** When present, the item becomes a clickable badge that opens a modal with the breakdown */
  breakdown?: Array<{ label: string; value: number; client?: string; pit?: string }>
}

export interface MetricCardAdjustment {
  /** Itemized list of deductions. Their sum is what's subtracted from data.real */
  items: MetricCardAdjustmentItem[]
  /** Label next to the adjusted value, e.g. "ajustado" */
  adjustedLabel?: string
}

/** Abbreviated BRL formatter — "R$ 1,37M", "R$ 850K", "R$ 420" */
function formatCurrencyCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) {
    return `R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`
  }
  if (abs >= 1_000) {
    return `R$ ${(abs / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`
  }
  return `R$ ${abs.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

interface EnhancedMetricCardProps {
  metric: string
  data: FinancialData
  section?: 'revenue' | 'cost'
  valueColor?: 'default' | 'white'
  /** Optional content rendered on the right side of the label row */
  headerRight?: React.ReactNode
  /** Optional override for the card title (defaults to labels[metric]) */
  label?: string
  /** Optional "foto do presente" adjustment rendered below the main value */
  adjustment?: MetricCardAdjustment
  /** Optional year-end projection trend result for inline badge + modal */
  trend?: TrendResult | null
  /** Optional content rendered below the main value (e.g. encarteirados badge) */
  afterValue?: React.ReactNode
  /** Optional content rendered above the trend/pace badge */
  aboveTrend?: React.ReactNode
  /** Period key for contextual notes (e.g. "2026-01-01_2026-03-31"). When provided, enables notes. */
  periodKey?: string
}

const EnhancedMetricCard = ({ metric, data, section = 'revenue', valueColor = 'default', headerRight, label, adjustment, trend, afterValue, aboveTrend, periodKey }: EnhancedMetricCardProps) => {
  const [trendModalOpen, setTrendModalOpen] = useState(false)
  const [insightModalOpen, setInsightModalOpen] = useState(false)
  const [breakdownModalItem, setBreakdownModalItem] = useState<MetricCardAdjustmentItem | null>(null)
  const [adjustmentOpen, setAdjustmentOpen] = useState(false)
  const metricLabel = label ?? labels[metric] ?? metric
  const insight = useMemo(
    () => generateInsight(metric, metricLabel, data, trend),
    [metric, metricLabel, data, trend]
  )

  if (!data) {
    return (
      <div className="metric-card">
        <div className="text-brand-gray">
          Dados não disponíveis
        </div>
      </div>
    )
  }

  const Icon = icons[metric] || DollarSign
  const hasProa = data.proa !== 0

  // All adjustment items are hidden "bad stuff" to account for. The math
  // is always `data.real - adjustmentTotal` because costs are stored as
  // negative numbers (see createFinancialMetric), so subtracting makes
  // them more negative (i.e., larger cost). Only the visual sign in the
  // list flips based on section: revenue/profit cards show "−R$ X",
  // cost cards show "+R$ X" — both representing the same "worse" move.
  const adjustmentTotal = adjustment?.items.reduce((s, it) => s + it.value, 0) ?? 0
  const hasAdjustment = !!adjustment && adjustmentTotal > 0
  const adjustedValue = hasAdjustment ? data.real - adjustmentTotal : data.real

  // Variance: recompute from the adjusted value when applicable. Previous
  // period stays raw because it is an already-settled snapshot (its own
  // historic data lag has long since caught up).
  //
  // For cost cards we compare ABSOLUTE values (matching createFinancialMetric
  // in financial-analytics.service.ts). That way "cost went up" shows as a
  // positive percentage, consistent with the raw service output.
  const variancePercent = (() => {
    if (!hasAdjustment || data.previousValue === undefined || data.previousValue === null) {
      return data.variance
    }
    const prev = Math.abs(data.previousValue)
    if (prev === 0) return data.variance
    if (section === 'cost') {
      return ((Math.abs(adjustedValue) - prev) / prev) * 100
    }
    return ((adjustedValue - data.previousValue) / prev) * 100
  })()
  // For cost cards, positive variance means "cost went up" — which is bad
  // news for the business. Flip the sign so the icon/color picker (which
  // thinks "positive = good") renders the down-red arrow in that case.
  const varianceForVisual = section === 'cost' ? -variancePercent : variancePercent
  const { Icon: VarianceIcon } = getVarianceIconData(varianceForVisual)

  return (
    <div className={`metric-card ${section === 'cost' ? 'metric-card-cost' : ''}`}>
      {/* Label row */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-xs font-medium text-brand-gray uppercase tracking-wide truncate">{label ?? labels[metric]}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {periodKey && (
            <CardNotesButton metric={metric} metricLabel={metricLabel} periodKey={periodKey} />
          )}
          {headerRight}
        </div>
      </div>

      {/* Valor principal */}
      <div className={`text-xl lg:text-2xl font-bold font-display mt-1 truncate ${data.real < 0 ? 'text-red-400' : valueColor === 'white' ? 'text-white' : 'text-brand-primary'}`}>
        <SensitiveValue mask="R$ ••••••">
          {data.real < 0 ? '-' : ''}{formatCurrency(data.real)}
        </SensitiveValue>
      </div>

      {/* Below-value slot (e.g. BU split) */}
      {aboveTrend}

      {/* Adjustment block (Lucro Ajustado et al.) */}
      {hasAdjustment && adjustment && (
        <div className="mt-3 text-xs">
          <button
            type="button"
            onClick={() => setAdjustmentOpen(!adjustmentOpen)}
            className="flex items-center justify-between w-full group"
          >
            <div className="flex items-baseline gap-2 truncate">
              <span className={`text-base lg:text-lg font-semibold font-display ${adjustedValue < 0 ? 'text-red-400' : 'text-brand-primary'}`}>
                <SensitiveValue mask="R$ ••••••">
                  {adjustedValue < 0 ? '-' : ''}{formatCurrency(adjustedValue)}
                </SensitiveValue>
              </span>
              <span className="text-[10px] font-medium text-brand-gray uppercase tracking-wide">
                ({adjustment.adjustedLabel ?? 'Ajustado'})
              </span>
            </div>
            <ChevronDown size={14} strokeWidth={1.5} className={`text-brand-gray flex-shrink-0 transition-transform duration-300 ease-out ${adjustmentOpen ? 'rotate-180' : ''}`} />
          </button>
          <div
            className="overflow-hidden transition-all duration-300 ease-out"
            style={{
              maxHeight: adjustmentOpen ? `${(adjustment.items.length + 1) * 32}px` : '0px',
              opacity: adjustmentOpen ? 1 : 0,
            }}
          >
            <div className="mt-2 space-y-1.5">
              {adjustment.items.map((item) => (
                <div key={item.label} className="flex justify-between items-center gap-2">
                  <span className="text-brand-gray truncate">{item.label}</span>
                  {item.breakdown ? (
                    <button
                      type="button"
                      onClick={() => setBreakdownModalItem(item)}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-dark-lighter border border-transparent hover:border-red-400/40 transition-colors text-red-400 font-bold whitespace-nowrap"
                    >
                      −<SensitiveValue mask="R$ ••••">
                        {formatCurrencyCompact(item.value)}
                      </SensitiveValue>
                      <HelpCircle size={11} strokeWidth={1.5} className="text-brand-gray" />
                    </button>
                  ) : (
                    <span className="text-brand-gray-light font-medium whitespace-nowrap">
                      −
                      <SensitiveValue mask="R$ ••••">
                        {formatCurrencyCompact(item.value)}
                      </SensitiveValue>
                      {item.isApproximate ? <span className="text-brand-gray"> (aprox.)</span> : null}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PROA section */}
      {hasProa && (() => {
        const isCost = data.real < 0
        // Use the adjusted value (when present) so the %PROA reflects the
        // honest "foto do presente" rather than the raw DRE number.
        const effectiveReal = hasAdjustment ? adjustedValue : data.real
        const absReal = Math.abs(effectiveReal)
        const absProa = Math.abs(data.proa)
        const proaPercent = (absReal / absProa) * 100
        const over100 = proaPercent >= 100
        const barColorClass = over100
          ? (section === 'cost' ? 'bg-red-400' : 'bg-brand-primary')
          : 'bg-gray-500'
        const colorClass = over100
          ? (section === 'cost' ? 'text-red-400' : 'text-brand-primary')
          : 'text-gray-400'

        return (
          <div className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-brand-gray">PROA</span>
              <span className="text-brand-gray-light font-medium">
                <SensitiveValue mask="R$ ••••••">
                  {formatCurrency(data.proa)}
                </SensitiveValue>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-brand-gray">{hasAdjustment ? '% AJUSTADO PROA' : '% PROA'}</span>
              <span className={`${colorClass} font-semibold`}>
                {proaPercent.toFixed(0)}%
              </span>
            </div>
            <div className="pt-0.5">
              <div className="w-full bg-brand-dark-lighter rounded-full h-[3px]">
                <div
                  className={`h-[3px] rounded-full transition-all duration-500 ${barColorClass}`}
                  style={{ width: `${Math.min(100, proaPercent)}%` }}
                />
              </div>
            </div>
            {/* Falta para o PROA */}
            {(() => {
              const falta = absProa - absReal
              if (falta <= 0) return null
              const now = new Date()
              const currentMonth = now.getMonth() // 0-based
              const mesesRestantes = 12 - currentMonth // inclui mês atual
              const porMes = mesesRestantes > 0 ? falta / mesesRestantes : falta
              return (
                <div className="flex justify-between items-center mt-1.5">
                  <span className="text-brand-gray">Falta</span>
                  <span className="text-brand-gray-light font-medium text-[10px]">
                    <SensitiveValue mask="R$ •••">
                      {formatCurrencyCompact(falta)}
                    </SensitiveValue>
                    <span className="text-brand-gray ml-1">
                      (<SensitiveValue mask="R$ •••">{formatCurrencyCompact(porMes)}</SensitiveValue>/mês)
                    </span>
                  </span>
                </div>
              )
            })()}
          </div>
        )
      })()}

      {/* After-value slot (e.g. encarteirados badge) */}
      {afterValue}

      {/* Divider */}
      <div className="border-t border-brand-dark-lighter my-3" />

      {/* Ano anterior + Variação */}
      <div className="space-y-1.5 text-xs">
        {data.previousValue !== undefined && (
          <div className="flex justify-between items-center">
            <span className="text-brand-gray">Período Anterior</span>
            <span className="text-brand-gray-light font-medium">
              <SensitiveValue mask="R$ ••••••">
                {data.previousValue < 0 ? '-' : ''}{formatCurrency(data.previousValue)}
              </SensitiveValue>
            </span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-brand-gray">Variação</span>
          <span className="font-semibold flex items-center gap-1">
            <span className="text-gray-400">{variancePercent > 0 ? '+' : ''}{variancePercent.toFixed(1)}%</span>
            <VarianceIcon size={12} strokeWidth={1.5} className={getVarianceColor(varianceForVisual)} />
          </span>
        </div>
      </div>

      {/* Trend projection badge */}
      {trend && (() => {
        const pct = Math.round(trend.projectedPercent * 100)
        const isCost = section === 'cost'

        // Cost-specific status config: labels and colors make sense for
        // "spending" semantics (under budget = green, over = red).
        // Revenue uses the standard TREND_STATUS_CONFIG.
        const COST_STATUS_CONFIG = {
          'above-track': { ...TREND_STATUS_CONFIG['above-track'], label: 'Abaixo do pace' },
          'on-track':    { ...TREND_STATUS_CONFIG['on-track'],    label: 'No pace' },
          'at-risk':     { ...TREND_STATUS_CONFIG['at-risk'],     label: 'Em risco' },
          'off-track':   { ...TREND_STATUS_CONFIG['off-track'],   label: 'Acima do pace' },
        }

        const status: 'above-track' | 'on-track' | 'at-risk' | 'off-track' = isCost
          ? (pct <= 95 ? 'above-track' : pct <= 103 ? 'on-track' : pct <= 110 ? 'at-risk' : 'off-track')
          : (pct >= 105 ? 'above-track' : pct >= 97 ? 'on-track' : pct >= 90 ? 'at-risk' : 'off-track')
        const cfg = isCost ? COST_STATUS_CONFIG[status] : TREND_STATUS_CONFIG[status]
        return (
          <div className="mt-3 pt-3 border-t border-brand-dark-lighter">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setInsightModalOpen(true)}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${cfg.bg} ${cfg.border} hover:ring-1 hover:ring-white/50 transition-all cursor-pointer`}
                aria-label={`Ver insights de ${metricLabel}`}
              >
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className={`absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-75 animate-ping`} />
                  <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                </span>
                <span className={`text-[10px] font-semibold ${cfg.color}`}>
                  {cfg.label}
                </span>
              </button>
              <span className="text-[10px] text-brand-gray">
                Dez/{trend.currentYear}:{' '}
                <span className="font-bold">
                  {pct}%
                </span>
                {' '}{trend.vsYear ? `vs ${trend.vsYear}` : 'PROA'}
              </span>
              <button
                type="button"
                onClick={() => setTrendModalOpen(true)}
                className="text-brand-gray hover:text-brand-primary transition-colors ml-auto"
                aria-label={`Como a projeção de ${metricLabel} é calculada`}
              >
                <HelpCircle size={14} strokeWidth={1.5} />
              </button>
            </div>
            <TrendExplanationModal
              open={trendModalOpen}
              onOpenChange={setTrendModalOpen}
              metricLabel={metricLabel}
              trend={trend}
            />
            <AIInsightModal
              open={insightModalOpen}
              onOpenChange={setInsightModalOpen}
              insight={insight}
              onOpenCalculation={() => setTrendModalOpen(true)}
            />
          </div>
        )
      })()}

      {/* Breakdown modal for adjustment items with detailed sub-items */}
      {breakdownModalItem && breakdownModalItem.breakdown && (
        <Dialog open={!!breakdownModalItem} onOpenChange={(open) => { if (!open) setBreakdownModalItem(null) }}>
          <DialogContent
            className="sm:max-w-4xl bg-brand-dark-light border-brand-dark-lighter text-white"
            overlayClassName="bg-black/80"
          >
            <DialogHeader>
              <DialogTitle className="text-white font-display text-xl">
                {breakdownModalItem.label}
              </DialogTitle>
              <DialogDescription className="text-brand-gray-light">
                Detalhamento dos custos já contratados mas ainda não lançados na DRE.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 text-sm mt-2">
              <div className="border border-brand-dark-lighter rounded-lg overflow-hidden">
                <table className="w-full text-[11px] sm:text-xs">
                  <thead className="bg-brand-dark">
                    <tr>
                      <th className="text-left px-3 py-2 text-brand-gray font-medium">Fornecedor</th>
                      <th className="text-left px-3 py-2 text-brand-gray font-medium">Cliente</th>
                      <th className="text-left px-3 py-2 text-brand-gray font-medium">PIT</th>
                      <th className="text-right px-3 py-2 text-brand-gray font-medium">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownModalItem.breakdown.map((sub) => (
                      <tr key={sub.label} className="border-t border-brand-dark-lighter">
                        <td className="px-3 py-2 font-medium text-white">{sub.label}</td>
                        <td className="px-3 py-2 text-brand-gray-light">{sub.client ?? '—'}</td>
                        <td className="px-3 py-2 text-brand-gray-light font-mono">{sub.pit ?? '—'}</td>
                        <td className="px-3 py-2 text-right text-white font-semibold tabular-nums">
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrency(sub.value)}
                          </SensitiveValue>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-brand-dark-lighter bg-brand-dark/40">
                      <td colSpan={3} className="px-3 py-2 font-bold text-white">Total</td>
                      <td className="px-3 py-2 text-right text-brand-primary font-bold tabular-nums">
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(breakdownModalItem.value)}
                        </SensitiveValue>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default EnhancedMetricCard
